import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link from "@insite/mobius/Link";
import React, { FC, useState } from "react";
import { ResolveThunks, connect } from "react-redux";
import { css } from "styled-components";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import getVisualNavigationManagerHandler from "@NA/Handlers/ADIVisualNavigationManagerHandler";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader from "@insite/mobius/DataTable/DataTableHeader";
import translate from "@insite/client-framework/Translate";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { StyledButton } from "@insite/mobius/Clickable";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import getColor from "@insite/mobius/utilities/getColor";
import ADIColors from "@NA/Utils/ADIColors";

const mapStateToProps = (state: ApplicationState) => ({
    isAuthenticated: state.context.session.isAuthenticated,
    location: getLocation(state),
});

const mapDispatchToProps = {
    getVisualNavigationManagerHandler
};

export interface ADIVisualNavigationManagerStyles {
    visualNavContainer?: InjectableCss;
    dataTableStyles?: DataTableProps;
    dataTableHeadStyles?: DataTableHeadProps;
    dataTableBodyStyles?: DataTableBodyProps;
}

export const adiVisualNavigationManagerStyles: ADIVisualNavigationManagerStyles = {
    dataTableBodyStyles: {
        css: css`
        background-color: transparent;
        >tr{
            &:nth-child(even){
                background: transparent;
            }
            background-color: transparent;
                td{
                    border: 1px solid ${ADIColors.gray3};
                    text-align: left;
                    display: table-cell;
                    line-height: 1.125rem;
                    color: ${getColor("text.link")};
                    font-size: 14px;
                    font-weight: 700;
                    padding: 9px 21px;
                    background: transparent;
                    height: auto;
                    white-space: normal;
                    >${StyledButton}{
                        >${TypographyStyle}{
                            &:hover{
                                color: ${getColor("text.link")};
                                text-decoration: underline;
                            }
                        }
                    }
                    &.category-col{
                        width: 200px;
                    }
                    &.category-col.start-date-val{
                        color: ${ADIColors.lightBlack3};
                    }
                }
        }
        `
    },
    dataTableHeadStyles: {
        css: css`
        background-color: transparent;
        th{
            border: 1px solid ${ADIColors.gray3};
            text-align: left;
            display: table-cell;
            line-height: 1.125rem;
            color: ${ADIColors.lightBlack3};
            font-size: 14px;
            font-weight: 700;
            padding: 9px 21px;
            background: transparent;
            height: auto;
            white-space: normal;
            &.category-col{
                width: 200px;
            }
        }
        `
    },
    dataTableStyles: {
        css: css`
        border: 1px solid ${ADIColors.gray3};
        background: ${getColor("common.background")};
        margin-bottom: 1.25rem;
        table-layout: auto;
        border-spacing: 0;
        `
    },
    visualNavContainer: {
        css: css`
        overflow: auto;
        max-width: 100%;
        flex-basis: 100%;
        >table{
            >thead{
                background-color: transparent;
            }
        }
        `
    }
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const styles = adiVisualNavigationManagerStyles;

const ADIVisualNavigationManager: FC<Props> = ({ getVisualNavigationManagerHandler, isAuthenticated,
    location }) => {
    const [visualNavData, setVisualNavData] = useState([]);

    React.useEffect(() => {
        getVisualNavigationManagerHandler({
            onSuccess: (result: any) => {
                setVisualNavData(result);
            }
        });
    }, []);

    React.useEffect(() => {
        if (!isAuthenticated && location.pathname.includes("MyAccount")) {
            const returnUrl = location.pathname;
            window.location.href = `/SignIn?returnUrl=${encodeURIComponent(returnUrl)}`;
        }
    }, []);

    if (!isAuthenticated) {
        return null;
    }

    return (
        <StyledWrapper {...styles.visualNavContainer}>
            <DataTable {...styles.dataTableStyles}>
                <DataTableHead
                    {...styles.dataTableHeadStyles}
                >
                    <DataTableHeader className="category-col">
                        {translate("Category")}
                    </DataTableHeader>
                    <DataTableHeader className="category-col">
                        {translate("Sub Category")}
                    </DataTableHeader>
                    <DataTableHeader>
                        {translate("Link")}
                    </DataTableHeader>
                    <DataTableHeader className="category-col">
                        {translate("Start Date")}
                    </DataTableHeader>
                    <DataTableHeader className="category-col">
                        {translate("End Date")}
                    </DataTableHeader>
                </DataTableHead>
                <DataTableBody
                    {...styles.dataTableBodyStyles}
                >
                    {visualNavData && visualNavData?.map((visualData: any) => (
                        <DataTableRow
                            key={visualData?.categoryName}
                        >
                            <DataTableCell className="category-col">
                                {visualData.categoryName}
                            </DataTableCell>
                            <DataTableCell className="category-col">
                                {visualData.subCategoryName}
                            </DataTableCell>
                            <DataTableCell>
                                <Link href={`${visualData.pageURL}`}>{visualData.pageURL}</Link>
                            </DataTableCell>
                            <DataTableCell className="category-col start-date-val">
                                {visualData.validFrom}
                            </DataTableCell>
                            <DataTableCell className="category-col start-date-val">
                                {visualData.validTo}
                            </DataTableCell>
                        </DataTableRow>
                    ))}
                </DataTableBody>
            </DataTable>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ADIVisualNavigationManager),
    definition: {
        displayName: "ADI Visual Navigation Manager",
        group: "Common",
    },
};

export default widgetModule;
